import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Clock, Package } from "lucide-react";

export default function Briefs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/briefs?status=open").then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-8 items-end">
          <div>
            <div className="overline text-safety">Lead marketplace</div>
            <h1 className="mt-3 font-display font-black text-4xl lg:text-6xl leading-none tracking-tighter text-forest">
              Clearance requests<br/> open for quotes
            </h1>
            <p className="mt-6 text-muted-foreground max-w-lg leading-relaxed">
              Offices closing down post a single clearance request. Vetted removal firms submit fixed
              quotes. Direct, transparent, no middleman.
            </p>
            <div className="mt-8 flex gap-3">
              <Button asChild data-testid="briefs-post" className="rounded-none bg-forest hover:bg-forest-800 text-white h-12 px-8">
                <Link to="/briefs/new">Post a request</Link>
              </Button>
              <Button asChild variant="outline" data-testid="briefs-my" className="rounded-none border-forest text-forest hover:bg-forest hover:text-white h-12 px-8">
                <Link to="/dashboard/briefs">My requests</Link>
              </Button>
            </div>
          </div>
          <div className="hidden lg:block border border-border bg-sage-light p-8">
            <div className="overline text-safety">How it works</div>
            <ol className="mt-4 space-y-3 text-sm">
              <li><span className="font-display text-forest font-black mr-2">1.</span>Post a clearance request with size, location & timing.</li>
              <li><span className="font-display text-forest font-black mr-2">2.</span>Removal firms in your area quote.</li>
              <li><span className="font-display text-forest font-black mr-2">3.</span>Pick the best quote & clear the office.</li>
            </ol>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
        {loading ? (
          <div className="grid grid-cols-1 gap-3">{[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-sage animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <div className="border border-border bg-white p-16 text-center">
            <div className="font-display text-2xl tracking-tight text-forest">No open requests right now</div>
            <p className="mt-2 text-sm text-muted-foreground">Be the first to post one — get quotes within hours.</p>
            <Button asChild className="mt-6 rounded-none bg-safety hover:bg-safety-hover text-white h-11 px-6" data-testid="briefs-empty-cta">
              <Link to="/briefs/new">Post a request →</Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {items.map((b) => (
              <Link
                to={`/briefs/${b.brief_id}`}
                key={b.brief_id}
                data-testid={`brief-card-${b.brief_id}`}
                className="group grid grid-cols-1 md:grid-cols-[1fr,180px] gap-4 border border-border bg-white p-6 hover:-translate-y-0.5 hover:shadow-lg transition-transform duration-200"
              >
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Badge className="rounded-none bg-forest text-white overline">{b.status}</Badge>
                    <Badge variant="outline" className="rounded-none border-border overline">{b.poster_type.replace("_", " ")}</Badge>
                  </div>
                  <div className="font-display text-xl tracking-tight text-forest group-hover:text-safety transition-colors duration-200">{b.title}</div>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{b.description}</p>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{b.location}{b.postcode ? ` · ${b.postcode}` : ""}</span>
                    <span className="flex items-center gap-1"><Package className="w-3.5 h-3.5" />{b.approx_items}</span>
                    {b.move_by_date && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Move by {b.move_by_date}</span>}
                  </div>
                </div>
                <div className="border-t md:border-t-0 md:border-l border-border pt-3 md:pt-0 md:pl-6 flex flex-col md:items-end justify-between">
                  {b.budget != null && (
                    <div className="text-right">
                      <div className="overline text-muted-foreground">Budget</div>
                      <div className="font-display text-2xl text-forest tabular-nums">£{Number(b.budget).toFixed(0)}</div>
                    </div>
                  )}
                  <div className="overline text-safety text-[10px]">{b.quote_count} quote{b.quote_count === 1 ? "" : "s"}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
